import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  Translate,
  ArrowRight,
  Stethoscope,
  Info,
  MapPin,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";
import {
  DOCTOR_MAPPINGS,
  FEATURED_BROWSE,
  STATE_DATA,
  findBestMatch,
  findByUrlSlug,
  getMappingByLabel,
  getStateBySlug,
  getStateByCode,
  stateCodeToSlug,
  cityToSlug,
  slugToCity,
  getAutocompleteSuggestions,
} from "../lib/doctorMappings";
import { webApplicationSchema } from "../lib/jsonLd";

const ITEMS_PER_PAGE = 20;
const SITE_URL = "https://npilookup.com";

const FindADoctor = () => {
  const { termSlug, stateSlug, citySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Resolve route params to data
  const mappingFromSlug = termSlug ? findByUrlSlug(termSlug) : null;
  const stateFromSlug = stateSlug ? getStateBySlug(stateSlug) : null;
  const cityFromSlug = citySlug ? slugToCity(citySlug) : "";

  // Handle legacy ?q= param — redirect to clean URL
  const legacyQ = searchParams.get("q");

  const [termInput, setTermInput] = useState("");
  const [stateCode, setStateCode] = useState(stateFromSlug?.code || "");
  const [cityInput, setCityInput] = useState(cityFromSlug || "");
  const [mapping, setMapping] = useState(mappingFromSlug);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [noMatch, setNoMatch] = useState(false);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // ---- Data fetching ----
  const fetchResults = useCallback(async (m, sc, city, page) => {
    if (!m) return;
    setLoading(true);
    try {
      const params = {
        taxonomy_description: m.taxonomy_description,
        limit: ITEMS_PER_PAGE,
        skip: (page - 1) * ITEMS_PER_PAGE,
      };
      if (sc) params.state = sc;
      if (city) params.city = city;

      const data = await api.searchProviders(params);
      setResults(data.results || []);
      setHasMore(data.has_more || false);
    } catch {
      setResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- Initialization from route params ----
  useEffect(() => {
    // Handle legacy ?q= redirect
    if (legacyQ && !termSlug) {
      const result = findBestMatch(legacyQ);
      if (result) {
        navigate(`/find-a-doctor/${result.match.url_slug}`, { replace: true });
      }
      return;
    }

    if (mappingFromSlug) {
      setMapping(mappingFromSlug);
      setTermInput(mappingFromSlug.layman_terms[0]);
      setStateCode(stateFromSlug?.code || "");
      setCityInput(cityFromSlug || "");
      setNoMatch(false);
      setCurrentPage(1);
      fetchResults(mappingFromSlug, stateFromSlug?.code || "", cityFromSlug, 1);
    } else if (termSlug) {
      // URL slug doesn't match any mapping
      setNoMatch(true);
      setTermInput(termSlug.replace(/-/g, " "));
      setMapping(null);
      setResults([]);
    } else {
      // Landing page
      setMapping(null);
      setResults([]);
      setNoMatch(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termSlug, stateSlug, citySlug]);

  // ---- Navigation helpers ----
  const navigateTo = (m, sc, city) => {
    let path = `/find-a-doctor/${m.url_slug}`;
    if (sc) {
      const slug = stateCodeToSlug(sc);
      path += `/${slug}`;
      if (city && city.trim()) {
        path += `/${cityToSlug(city.trim())}`;
      }
    }
    navigate(path);
  };

  const handleSearch = () => {
    const q = termInput.trim();
    if (!q) return;
    const result = findBestMatch(q);
    if (result) {
      navigateTo(result.match, stateCode, cityInput);
    } else {
      setNoMatch(true);
      setMapping(null);
      setResults([]);
      navigate(`/find-a-doctor/${q.toLowerCase().replace(/\s+/g, "-")}`);
    }
  };

  const handleBrowseClick = (label) => {
    const m = getMappingByLabel(label);
    if (m) navigateTo(m, "", "");
  };

  const handleStateChange = (value) => {
    const sc = value === "all" ? "" : value;
    setStateCode(sc);
    setCityInput("");
    if (mapping) navigateTo(mapping, sc, "");
  };

  const handleCitySearch = () => {
    if (mapping && stateCode) navigateTo(mapping, stateCode, cityInput);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchResults(mapping, stateCode, cityFromSlug, newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- Autocomplete ----
  const handleInputChange = (e) => {
    const val = e.target.value;
    setTermInput(val);
    setSelectedIdx(-1);
    if (val.trim().length >= 2) {
      const s = getAutocompleteSuggestions(val);
      setSuggestions(s);
      setShowSuggestions(s.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (item) => {
    setTermInput(item.mapping.layman_terms[0]);
    setShowSuggestions(false);
    navigateTo(item.mapping, stateCode, cityInput);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === "Enter") handleSearch();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIdx >= 0 && suggestions[selectedIdx]) {
        handleSelectSuggestion(suggestions[selectedIdx]);
      } else {
        setShowSuggestions(false);
        handleSearch();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ---- SEO Data ----
  const isLanding = !termSlug;
  const hasState = !!stateFromSlug;
  const hasCity = !!citySlug;
  const stateName = stateFromSlug?.name || "";
  const cityName = cityFromSlug || "";

  const buildTitle = () => {
    if (isLanding) return "Find a Doctor — NPI Lookup by Plain English Search";
    if (!mapping) return `Find a Doctor — NPI Lookup`;
    const base = `${mapping.browse_label} NPI Lookup`;
    if (hasCity) return `${base} in ${cityName}, ${stateName} — Find Doctors | NPI Lookup`;
    if (hasState) return `${base} in ${stateName} — Doctor NPI Directory`;
    return `${base} — Find ${mapping.official_name} NPI Numbers`;
  };

  const buildDescription = () => {
    if (isLanding) return "Don't know the medical term? Search the NPI registry in plain English — type 'heart doctor' or 'knee pain' and we'll match the correct specialist and return verified NPI numbers.";
    if (!mapping) return "Find a doctor by plain English — NPI lookup translates everyday terms to medical specialties and returns verified providers from the NPPES registry.";
    const base = `NPI lookup for ${mapping.layman_terms[0]} (${mapping.official_name})`;
    if (hasCity) return `${base} in ${cityName}, ${stateName}. Verified NPI numbers, specialty data, and NPPES credentials for doctors treating ${mapping.condition_terms.slice(0, 3).join(", ")}.`;
    if (hasState) return `${base} in ${stateName}. Search verified ${mapping.taxonomy_description} providers with NPI numbers from the national NPPES registry.`;
    return `${base} — find verified providers with NPI numbers treating ${mapping.condition_terms.slice(0, 3).join(", ")} in the NPPES national registry.`;
  };

  const buildJsonLd = () => {
    const ld = [];

    // BreadcrumbList
    const crumbs = [{ name: "Home", url: SITE_URL }, { name: "Find a Doctor", url: `${SITE_URL}/find-a-doctor` }];
    if (mapping) crumbs.push({ name: mapping.browse_label, url: `${SITE_URL}/find-a-doctor/${mapping.url_slug}` });
    if (hasState) crumbs.push({ name: stateName, url: `${SITE_URL}/find-a-doctor/${mapping.url_slug}/${stateSlug}` });
    if (hasCity) crumbs.push({ name: cityName, url: `${SITE_URL}/find-a-doctor/${mapping.url_slug}/${stateSlug}/${citySlug}` });

    ld.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": crumbs.map((c, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": c.name,
        "item": c.url,
      })),
    });

    // MedicalWebPage for specialty pages
    if (mapping) {
      const page = {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        "name": buildTitle(),
        "description": buildDescription(),
        "about": { "@type": "MedicalSpecialty", "name": mapping.taxonomy_description },
      };
      if (hasState) page.areaServed = { "@type": "State", "name": stateName };
      if (hasCity) page.areaServed = { "@type": "City", "name": cityName, "containedInPlace": { "@type": "State", "name": stateName } };
      ld.push(page);
    }

    // WebApplication schema for the tool itself
    if (!mapping) {
      ld.push(webApplicationSchema());
    }

    return ld;
  };

  const canonicalUrl = () => {
    let path = "/find-a-doctor";
    if (mapping) path += `/${mapping.url_slug}`;
    if (hasState) path += `/${stateSlug}`;
    if (hasCity) path += `/${citySlug}`;
    return `${SITE_URL}${path}`;
  };

  // ---- Render ----
  return (
    <>
      <Helmet>
        <title>{buildTitle()}</title>
        <meta name="description" content={buildDescription()} />
        <link rel="canonical" href={canonicalUrl()} />
        {buildJsonLd().map((ld, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
        ))}
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="relative bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }} />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-3">
                <Translate className="w-4 h-4 text-teal-300" weight="bold" />
                <span className="text-teal-200 text-sm font-medium">Plain English Search</span>
              </div>
              <h1
                className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-2"
                data-testid="find-doctor-title"
              >
                {mapping && !isLanding
                  ? hasCity
                    ? `${mapping.browse_label} in ${cityName}, ${stateName}`
                    : hasState
                    ? `${mapping.browse_label} in ${stateName}`
                    : `Find a ${mapping.browse_label}`
                  : "Find a Doctor"}
              </h1>
              <p className="text-slate-300 text-base max-w-xl mx-auto">
                {mapping
                  ? `Showing ${mapping.official_name}s (${mapping.taxonomy_description})${hasState ? ` in ${stateName}` : ""}`
                  : "Don't know the medical term? Just describe what you need."}
              </p>
            </div>

            {/* Search Form */}
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl p-2 shadow-2xl shadow-black/20">
                <div className="flex flex-col gap-2">
                  {/* Row 1: Specialty input + Search button */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1 relative" ref={inputRef}>
                      <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10" />
                      <Input
                        type="text"
                        placeholder='e.g. "heart doctor", "knee pain", "anxiety"...'
                        value={termInput}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                        className="h-12 pl-11 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl"
                        data-testid="find-doctor-input"
                        autoComplete="off"
                      />
                      {/* Autocomplete dropdown */}
                      {showSuggestions && (
                        <div
                          ref={suggestionsRef}
                          className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
                          data-testid="autocomplete-dropdown"
                        >
                          {suggestions.map((item, i) => (
                            <button
                              key={item.mapping.url_slug}
                              onClick={() => handleSelectSuggestion(item)}
                              className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                                i === selectedIdx ? "bg-teal/5" : "hover:bg-slate-50"
                              } ${i > 0 ? "border-t border-slate-100" : ""}`}
                              data-testid={`suggestion-${item.mapping.url_slug}`}
                            >
                              <div>
                                <p className="text-sm font-medium text-slate-900">{item.text}</p>
                                <p className="text-xs text-slate-400">{item.mapping.taxonomy_description}</p>
                              </div>
                              <span className="text-xs text-teal font-medium bg-teal/5 px-2 py-0.5 rounded-full shrink-0 ml-3">
                                {item.mapping.official_name}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleSearch}
                      disabled={loading || !termInput.trim()}
                      className="h-12 px-8 bg-teal hover:bg-teal/90 text-white font-semibold rounded-xl shrink-0"
                      data-testid="find-doctor-search-btn"
                    >
                      {loading ? "Searching..." : "Search"}
                    </Button>
                  </div>
                  {/* Row 2: State + City filters */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={stateCode || "all"} onValueChange={handleStateChange}>
                      <SelectTrigger
                        className="h-10 w-full sm:w-48 border-0 bg-slate-50 rounded-xl text-sm"
                        data-testid="find-doctor-state-filter"
                      >
                        <SelectValue placeholder="Filter by state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All states</SelectItem>
                        {STATE_DATA.map(s => (
                          <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {stateCode && (
                      <div className="flex gap-2 flex-1">
                        <Input
                          type="text"
                          placeholder="City (optional)"
                          value={cityInput}
                          onChange={(e) => setCityInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleCitySearch()}
                          className="h-10 text-sm border-0 bg-slate-50 rounded-xl flex-1"
                          data-testid="find-doctor-city-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCitySearch}
                          className="h-10 px-4 rounded-xl"
                          data-testid="find-doctor-city-btn"
                        >
                          <MapPin className="w-4 h-4 mr-1" /> Go
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {isLanding && (
                <p className="text-slate-400 text-xs text-center mt-3">
                  Try: "stomach doctor", "back pain", "children's doctor", "skin rash", "breathing problems"
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Breadcrumbs */}
        {!isLanding && (
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-sm" data-testid="find-doctor-breadcrumbs">
            <ol className="flex items-center gap-2 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li><Link to="/find-a-doctor" className="hover:text-teal">Find a Doctor</Link></li>
              {mapping && (
                <>
                  <li>/</li>
                  <li>
                    {hasState ? (
                      <Link to={`/find-a-doctor/${mapping.url_slug}`} className="hover:text-teal">{mapping.browse_label}</Link>
                    ) : (
                      <span className="text-slate-900 font-medium">{mapping.browse_label}</span>
                    )}
                  </li>
                </>
              )}
              {hasState && (
                <>
                  <li>/</li>
                  <li>
                    {hasCity ? (
                      <Link to={`/find-a-doctor/${mapping.url_slug}/${stateSlug}`} className="hover:text-teal">{stateName}</Link>
                    ) : (
                      <span className="text-slate-900 font-medium">{stateName}</span>
                    )}
                  </li>
                </>
              )}
              {hasCity && (
                <>
                  <li>/</li>
                  <li className="text-slate-900 font-medium">{cityName}</li>
                </>
              )}
            </ol>
          </nav>
        )}

        {/* Translation Banner + Results */}
        {mapping && !isLanding && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Translation Banner */}
            <div className="mb-5 rounded-xl border-2 border-teal/20 bg-teal/5 p-4" data-testid="translation-banner">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Translate className="w-5 h-5 text-teal" weight="bold" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">
                    Showing <span className="font-bold text-teal">{mapping.official_name}s</span>{" "}
                    ({mapping.taxonomy_description}) &mdash; the specialists who treat{" "}
                    {mapping.condition_terms.slice(0, 3).join(", ")}
                    {hasState && <span> in <strong>{stateName}</strong></span>}
                    {hasCity && <span>, <strong>{cityName}</strong></span>}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    Also known as: {mapping.layman_terms.join(", ")}
                    {" "}&middot;{" "}
                    <Link to={`/specialty/${mapping.official_slug}`} className="text-teal hover:underline">
                      View full {mapping.taxonomy_description} directory
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Results */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="skeleton h-4 w-20 mb-3 rounded" />
                    <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                    <div className="skeleton h-4 w-1/2 mb-4 rounded" />
                    <div className="skeleton h-10 w-full rounded" />
                  </div>
                ))}
              </div>
            ) : results.length > 0 ? (
              <>
                <p className="text-sm text-slate-500 mb-4" data-testid="find-doctor-result-count">
                  Showing {results.length} {mapping.official_name}s per page
                  {hasState && ` in ${stateName}`}{hasCity && `, ${cityName}`}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="find-doctor-results-grid">
                  {results.map(provider => (
                    <ProviderCard key={provider.number} provider={provider} />
                  ))}
                </div>
                {(hasMore || currentPage > 1) && (
                  <div className="flex items-center justify-center gap-3 mt-8" data-testid="find-doctor-pagination">
                    <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                      <CaretLeft className="w-4 h-4 mr-1" /> Previous
                    </Button>
                    <span className="text-sm text-slate-500 font-medium px-3">Page {currentPage}</span>
                    <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={!hasMore}>
                      Next <CaretRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                <Stethoscope className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <h3 className="font-display font-semibold text-lg text-slate-900 mb-1">No providers found</h3>
                <p className="text-slate-500 text-sm">
                  {hasCity
                    ? `No ${mapping.official_name}s found in ${cityName}, ${stateName}. Try a different city or `
                    : hasState
                    ? `No ${mapping.official_name}s found in ${stateName}. Try `
                    : "No results. Try "}
                  <Link to={`/find-a-doctor/${mapping.url_slug}`} className="text-teal hover:underline">removing filters</Link>.
                </p>
              </div>
            )}
          </section>
        )}

        {/* No-match banner */}
        {noMatch && !isLanding && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6 rounded-xl border-2 border-amber-200 bg-amber-50 p-5" data-testid="no-match-banner">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" weight="fill" />
                <div>
                  <p className="font-medium text-amber-900">
                    We couldn't match "{termSlug?.replace(/-/g, " ")}" to a known specialty.
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    Try a different description, or browse from the common terms below.
                    You can also{" "}
                    <Link to={`/search?q=${encodeURIComponent(termSlug?.replace(/-/g, " ") || "")}`} className="underline font-medium">
                      search the full NPI directory
                    </Link>.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ====== State Internal Links (when on specialty page without state) ====== */}
        {mapping && !hasState && !isLanding && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-navy" />
                <h2 className="font-display font-semibold text-xl text-slate-900">
                  Find {mapping.browse_label} by State
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-5">
                Browse {mapping.official_name}s in every US state
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="state-links-grid">
                {STATE_DATA.map(st => (
                  <Link
                    key={st.code}
                    to={`/find-a-doctor/${mapping.url_slug}/${st.slug}`}
                    className="px-3 py-2 text-sm bg-slate-50 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-700 transition-colors text-center"
                    title={`Find ${mapping.browse_label} in ${st.name}`}
                  >
                    {st.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ====== Other States (when on specialty+state page) ====== */}
        {mapping && hasState && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-display font-semibold text-lg text-slate-900 mb-4">
                {mapping.browse_label} in Other States
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="other-states-grid">
                {STATE_DATA.map(st => (
                  <Link
                    key={st.code}
                    to={`/find-a-doctor/${mapping.url_slug}/${st.slug}`}
                    className={`px-3 py-2 text-sm rounded-lg text-center transition-colors ${
                      st.code === stateCode
                        ? "bg-navy text-white"
                        : "bg-slate-50 text-slate-600 hover:bg-teal/10 hover:text-teal"
                    }`}
                  >
                    {st.code}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ====== Cross-linking: Other specialties in same location ====== */}
        {mapping && hasState && !isLanding && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-display font-semibold text-lg text-slate-900 mb-2">
                Other Specialists{hasCity ? ` in ${cityName}, ${stateName}` : ` in ${stateName}`}
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Looking for a different type of doctor? Browse all specialties{hasCity ? ` in ${cityName}` : ` in ${stateName}`}.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="cross-specialty-links">
                {DOCTOR_MAPPINGS.filter(m => m.url_slug !== mapping.url_slug).slice(0, 16).map(m => (
                  <Link
                    key={m.url_slug}
                    to={hasCity
                      ? `/find-a-doctor/${m.url_slug}/${stateSlug}/${citySlug}`
                      : `/find-a-doctor/${m.url_slug}/${stateSlug}`
                    }
                    className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 hover:bg-teal/10 hover:text-teal transition-colors"
                    title={`Find ${m.browse_label}${hasCity ? ` in ${cityName}, ${stateName}` : ` in ${stateName}`}`}
                  >
                    {m.browse_label}
                  </Link>
                ))}
              </div>
              <div className="mt-3 text-center">
                <Link to="/directory" className="text-xs text-teal font-medium hover:underline">
                  View complete directory of all specialties and states &rarr;
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ====== Browse Grid ====== */}
        <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isLanding ? "py-12" : "pb-10"}`}>
          <div className="text-center mb-8">
            <h2 className="font-display font-semibold text-2xl text-slate-900 mb-2" data-testid="browse-grid-title">
              {isLanding ? "What kind of doctor do you need?" : "Browse Other Specialties"}
            </h2>
            <p className="text-slate-500 text-sm max-w-lg mx-auto">
              Click any card to find verified providers. No medical jargon needed.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="browse-grid">
            {FEATURED_BROWSE.map(label => {
              const m = getMappingByLabel(label);
              if (!m) return null;
              const isActive = mapping?.url_slug === m.url_slug;
              return (
                <Link
                  key={label}
                  to={`/find-a-doctor/${m.url_slug}`}
                  className={`group relative text-left rounded-xl border p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
                    isActive ? "border-teal bg-teal/5 shadow-sm ring-1 ring-teal/20" : `${m.color} hover:border-teal/40`
                  }`}
                  data-testid={`browse-card-${m.url_slug}`}
                  title={`Find a ${label}`}
                >
                  <p className={`font-semibold text-sm mb-1 ${isActive ? "text-teal" : ""}`}>{label}</p>
                  <p className="text-xs text-slate-400 leading-tight">{m.taxonomy_description}</p>
                  <ArrowRight className={`absolute top-4 right-3 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? "text-teal" : "text-slate-400"}`} />
                </Link>
              );
            })}
          </div>
        </section>

        {/* ====== Landing-only SEO sections ====== */}
        {isLanding && (
          <>
            {/* How It Works */}
            <section className="bg-slate-50 py-12">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="font-display font-semibold text-xl text-slate-900 text-center mb-8">
                  How "Find a Doctor" Works
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { num: "1", color: "bg-navy/10 text-navy", title: "Describe Your Need", text: 'Type what you need in plain English \u2014 "heart doctor", "knee pain", or even a condition like "anxiety".' },
                    { num: "2", color: "bg-teal/10 text-teal", title: "We Translate It", text: "Our system maps your words to the correct official medical specialty used in the national NPI registry." },
                    { num: "3", color: "bg-emerald-500/10 text-emerald-600", title: "See Real Providers", text: "Browse verified, NPI-registered providers. Filter by state and city to find care near you." },
                  ].map(step => (
                    <div key={step.num} className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                      <div className={`w-10 h-10 rounded-full ${step.color} flex items-center justify-center mx-auto mb-3`}>
                        <span className="font-display font-bold">{step.num}</span>
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm mb-1">{step.title}</h3>
                      <p className="text-slate-500 text-xs leading-relaxed">{step.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Complete Translation Table */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h2 className="font-display font-semibold text-xl text-slate-900 mb-2">
                Complete Specialty Translation Guide
              </h2>
              <p className="text-slate-500 text-sm mb-6">
                Every medical specialty and the plain-English terms patients commonly use.
              </p>
              <div className="overflow-hidden rounded-xl border border-slate-200">
                <table className="w-full text-sm" data-testid="specialty-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">You Might Say</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Official Specialist</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden sm:table-cell">Common Conditions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DOCTOR_MAPPINGS.map((m, i) => (
                      <tr key={m.url_slug} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                        <td className="px-4 py-2.5">
                          <Link to={`/find-a-doctor/${m.url_slug}`} className="text-slate-700 hover:text-teal">
                            {m.layman_terms.slice(0, 2).join(", ")}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5">
                          <Link to={`/find-a-doctor/${m.url_slug}`} className="text-teal hover:underline font-medium">
                            {m.official_name}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell">
                          {m.condition_terms.slice(0, 3).join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* FAQ */}
            <section className="bg-slate-50 py-12">
              <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="font-display font-semibold text-xl text-slate-900 mb-6 text-center">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3" data-testid="find-doctor-faq">
                  {[
                    { q: "Why can't I just search 'heart doctor' on the regular search?", a: "The NPI Registry uses official medical taxonomy terms like 'Cardiology' \u2014 not everyday language. Our Find a Doctor tool automatically translates your plain English search into the right term so you get accurate results." },
                    { q: "How accurate is the translation?", a: "We map over 100 common terms and conditions to their correct medical specialties. The translation covers the vast majority of what patients search for. If your term isn't recognized, we'll suggest alternatives." },
                    { q: "Are these real, verified doctors?", a: "Yes. Every provider shown has a valid NPI (National Provider Identifier) registered with the Centers for Medicare & Medicaid Services (CMS). We search the official NPPES database in real time." },
                    { q: "Can I filter by state and city?", a: "Absolutely. Use the state dropdown and city input to find specialists in your area. The URL updates to a clean, shareable address like /find-a-doctor/heart-doctor/california/los-angeles." },
                  ].map((faq, i) => (
                    <details key={i} className="group bg-white border border-slate-200 rounded-lg">
                      <summary className="flex items-center justify-between p-4 cursor-pointer select-none hover:bg-slate-50/50 transition-colors rounded-lg">
                        <span className="font-medium text-slate-900 text-sm pr-4">{faq.q}</span>
                        <CaretRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
                      </summary>
                      <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
                    </details>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {/* ====== Specialty-page SEO content ====== */}
        {mapping && !isLanding && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
                About {mapping.official_name}s{hasState ? ` in ${stateName}` : ""}
              </h2>
              <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                <p>
                  A <strong>{mapping.official_name}</strong> specializes in <strong>{mapping.taxonomy_description}</strong>.
                  Patients commonly search for them using terms like{" "}
                  {mapping.layman_terms.map((t, i) => (
                    <span key={i}>{i > 0 ? ", " : ""}<em>"{t}"</em></span>
                  ))}.
                </p>
                <p>
                  {mapping.official_name}s treat conditions including{" "}
                  {mapping.condition_terms.join(", ")}.
                  {hasState
                    ? ` You can find verified ${mapping.official_name}s in ${stateName} through the NPI Registry.`
                    : ` Use the state filter above to narrow results to your area.`}
                </p>
                <p>
                  Every provider listed has a valid National Provider Identifier (NPI) registered with the
                  Centers for Medicare & Medicaid Services (CMS). Use our{" "}
                  <Link to="/verify" className="text-teal hover:underline">Verification Report</Link>{" "}
                  tool to generate a full credentialing document for any provider.
                </p>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default FindADoctor;
